import { faqJsonLd, articleJsonLd, stripTags } from "@/lib/seo-jsonld";
export { faqJsonLd, articleJsonLd };
import type { ReactNode } from "react";
import { MiniTasteTest } from "@/components/MiniTasteTest";
import { Link } from "@/components/LocalizedLink";
import { CheckCircle2, ArrowRight, MapPin, Phone, FileCheck2, Clock3, Receipt, ExternalLink } from "lucide-react";
import { Disclaimer } from "./Disclaimer";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";

export type LocalData = {
  unit: string;
  address?: string;
  phone?: string;
  feeExamples: string[];
  waitTime: string;
  checklist: string[];
  officialUrl: { label: string; href: string };
};

// Bygger GovernmentOrganization-schema från exakt de lokala uppgifter som
// visas på sidan (namn, adress, telefon, officiell länk). Inget påhittat.
function localOrgSchema(localData: LocalData) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "GovernmentOrganization",
    name: localData.unit,
    url: localData.officialUrl.href,
    areaServed: { "@type": "Country", name: "Sverige" },
  };
  if (localData.phone) schema.telephone = localData.phone;
  if (localData.address) {
    // Format: "Gatunamn 1, 118 27 Stockholm"
    const [street, rest] = localData.address.split(/,\s*(.+)/);
    const postal: Record<string, unknown> = {
      "@type": "PostalAddress",
      streetAddress: street?.trim(),
      addressCountry: "SE",
    };
    if (rest) {
      const m = rest.trim().match(/^(\d{3}\s?\d{2})\s+(.+)$/);
      if (m) {
        postal.postalCode = m[1];
        postal.addressLocality = m[2];
      } else {
        postal.addressLocality = rest.trim();
      }
    }
    schema.address = postal;
  }
  return schema;
}


export function SeoPage({
  title,
  intro,
  sections,
  faqs,
  related,
  sources,
  breadcrumbs,
  lastReviewed,
  localData,
  offerBlock,
}: {
  title: string;
  intro: string;
  sections: { h2: string; body: string }[];
  faqs: { q: string; a: string }[];
  related?: { title: string; links: { to: string; label: string }[] };
  sources?: { label: string; href: string }[];
  breadcrumbs?: Crumb[];
  lastReviewed?: string;
  localData?: LocalData;
  /** Valfritt konverteringsblock som visas direkt under sidans CTA:er. */
  offerBlock?: ReactNode;
}) {

  return (
    <article className="container-narrow py-12 sm:py-16">
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex rounded-full bg-secondary px-3 py-1 text-xs">Uppdaterat för 2026</span>
        {lastReviewed && (
          <span className="inline-flex rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
            Senast granskad <time dateTime={lastReviewed}>{lastReviewed}</time>
          </span>
        )}
      </div>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-balance">{title}</h1>
      <p className="mt-4 text-lg text-muted-foreground text-balance">{intro}</p>


      <div className="flex flex-col">
      <div className="order-2 mt-6 sm:order-1 sm:mt-8 flex flex-wrap gap-3">
        <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-5 py-3 text-sm font-medium text-accent hover:bg-accent/15">
          {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="order-1 mt-5 sm:order-2 sm:mt-8">{offerBlock ?? <MiniTasteTest />}</div>
      </div>

      <aside className="mt-6 rounded-xl border border-accent/30 bg-gradient-to-br from-accent/10 to-primary/5 p-4 sm:p-5 text-sm">
        <strong className="font-display">Tips:</strong> Gör vårt <Link to="/gratis-ovningsfragor-serveringstillstand" className="text-accent underline underline-offset-2 hover:no-underline">gratis provtest för serveringstillstånd</Link> — 5 riktiga frågor utan registrering. Eller hoppa direkt in i ett <Link to="/ovningsprov-serveringstillstand" className="text-accent underline underline-offset-2 hover:no-underline">övningsprov</Link>.
      </aside>

      {localData && (
        <section className="mt-10 rounded-2xl border border-border bg-card p-6">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(localOrgSchema(localData)) }}
          />
          <h2 className="font-display text-xl font-semibold">Lokala uppgifter</h2>

          <div className="mt-4 grid gap-5 sm:grid-cols-2">
            <div>
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <div>
                  <div className="font-medium">{localData.unit}</div>
                  {localData.address && <div className="text-muted-foreground">{localData.address}</div>}
                </div>
              </div>
              {localData.phone && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-accent" />
                  <a href={`tel:${localData.phone.replace(/\s+/g, "")}`} className="hover:underline">{localData.phone}</a>
                </div>
              )}
              <div className="mt-3 flex items-start gap-2 text-sm">
                <Clock3 className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <div>
                  <div className="font-medium">Handläggningstid</div>
                  <div className="text-muted-foreground">{localData.waitTime}</div>
                </div>
              </div>
              <a
                href={localData.officialUrl.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm text-accent hover:underline"
              >
                <ExternalLink className="h-4 w-4" /> {localData.officialUrl.label}
              </a>
            </div>
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <Receipt className="h-4 w-4 text-accent" /> Avgifter (exempel)
              </div>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {localData.feeExamples.map((f) => (
                  <li key={f}>• {f}</li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-2 text-sm font-medium">
                <FileCheck2 className="h-4 w-4 text-accent" /> Dokumentchecklista
              </div>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {localData.checklist.map((c) => (
                  <li key={c}>• {c}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Avgifter och rutiner kan ändras – verifiera alltid mot kommunens webbplats.
          </p>
        </section>
      )}

      <div className="mt-12 space-y-10">
        {sections.map((s) => (
          <section key={s.h2}>
            <h2 className="font-display text-2xl font-semibold">{s.h2}</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed whitespace-pre-line">{s.body}</p>
            <div className="mt-4">
              <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </section>
        ))}
      </div>

      {related && (
        <section className="mt-12 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold">{related.title}</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {related.links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="inline-flex items-center gap-2 text-accent hover:underline text-sm">
                  <ArrowRight className="h-4 w-4" /> {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-semibold">Mer från kunskapsbanken</h2>
        <p className="mt-1 text-sm text-muted-foreground">Populära guider för dig som ska söka serveringstillstånd eller plugga kunskapsprovet.</p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {[
            { to: "/kunskapsbank/serveringstillstand-2026-guide", label: "Serveringstillstånd 2026 – komplett guide" },
            { to: "/kunskapsbank/nya-alkohollagen-2026", label: "Nya alkohollagen 2026" },
            { to: "/kunskapsbank/alkoholtillstand-utan-mat", label: "Alkoholtillstånd utan mat" },
            { to: "/kunskapsbank/ansoka-om-alkoholtillstand", label: "Ansöka om alkoholtillstånd – steg för steg" },
            { to: "/kunskapsbank/alkoholtillstand-regler", label: "Alkoholtillstånd – regler" },
            { to: "/kunskapsbank/hur-manga-ratt-kunskapsprovet", label: "Hur många rätt krävs på kunskapsprovet?" },
            { to: "/kunskapsbank/vanliga-misstag-kunskapsprovet", label: "Vanliga misstag på kunskapsprovet" },
            { to: "/kunskapsbank/kunskapsprovet-exempelfragor", label: "Exempelfrågor från kunskapsprovet" },
            { to: "/kunskapsbank/plugga-kunskapsprovet", label: "Plugga inför kunskapsprovet – 4-veckorsplan" },
            { to: "/kunskapsbank/vad-kostar-serveringstillstand", label: "Vad kostar ett serveringstillstånd?" },
            { to: "/gratis-ovningsfragor-serveringstillstand", label: "Gratis övningsfrågor – riktigt provtest" },
            { to: "/kunskapsbank", label: "Se hela kunskapsbanken →" },
          ].map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex items-center gap-2 text-accent hover:underline text-sm">
                <ArrowRight className="h-4 w-4" /> {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {sources && sources.length > 0 && (
        <section id="kallor" className="mt-12 rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold">Officiella källor</h2>
          {lastReviewed && (
            <p className="mt-2 text-xs text-muted-foreground">
              Senast granskad <time dateTime={lastReviewed}>{lastReviewed}</time>. Kontrollera alltid aktuell information hos respektive myndighet eller kommun.
            </p>
          )}
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {sources.map((s) => (
              <li key={s.href}>
                <a href={s.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-accent hover:underline text-sm">
                  <ArrowRight className="h-4 w-4" /> {s.label}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}


      <section className="mt-12">

        <h2 className="font-display text-2xl font-semibold">Vanliga frågor</h2>
        <div className="mt-4 space-y-3">
          {faqs.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
              <summary className="cursor-pointer list-none flex justify-between gap-4 font-medium">
                <span>{f.q}</span>
                <span className="text-accent transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="mt-12 rounded-2xl bg-surface-deep text-primary-foreground p-8 text-center">
        <h2 className="font-display text-2xl font-bold">Redo att börja öva?</h2>
        <p className="mt-2 text-primary-foreground/80">Börja gratis utan kort. Lås upp obegränsat med Premium.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-white text-primary px-6 py-3 text-sm font-medium hover:bg-white/90">
            {FREE_CTA.label} <CheckCircle2 className="h-4 w-4" />
          </Link>
          <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-white/30 text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-white/10">
            {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="mt-10">
        <Disclaimer />
      </div>
    </article>
  );
}


/**
 * Article JSON-LD för artikel-stil-URL:er (kunskapsbank, lagsidor, "nya regler"-sidor).
 * BreadcrumbList har egen helper i Breadcrumbs.tsx; Product/Offer ligger på /pricing;
 * Organization/WebSite ligger globalt i __root.tsx.
 */

